import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardFooter } from './ui/card';
import { Button } from './ui/button';
import { ScrollArea } from './ui/scroll-area';
import { Shield, AlertTriangle } from 'lucide-react';
import logger from '../utils/logger';

const texts = {
  es: {
    title: 'Aviso de Privacidad',
    subtitle: 'Para continuar usando la plataforma, es necesario que leas y aceptes el siguiente aviso de privacidad.',
    accept: 'Acepto el Aviso de Privacidad',
    decline: 'No acepto',
    declineWarning: 'Si no aceptas el aviso de privacidad, no podrás acceder a la plataforma. Tu sesión será cerrada.',
    confirmDecline: 'Cerrar sesión',
    cancelDecline: 'Volver',
    version: 'Versión',
    jurisdiction: 'Jurisdicción',
    scrollToAccept: 'Desplázate hasta el final para poder aceptar',
    loading: 'Verificando consentimiento...'
  },
  en: {
    title: 'Privacy Notice',
    subtitle: 'To continue using the platform, you need to read and accept the following privacy notice.',
    accept: 'I Accept the Privacy Notice',
    decline: 'I Decline',
    declineWarning: 'If you decline the privacy notice, you will not be able to access the platform. Your session will be closed.',
    confirmDecline: 'Close Session',
    cancelDecline: 'Go Back',
    version: 'Version',
    jurisdiction: 'Jurisdiction',
    scrollToAccept: 'Scroll to the bottom to accept',
    loading: 'Verifying consent...'
  }
};

const ConsentScreen = ({ onConsentGiven }) => {
  const { user, logout } = useAuth();
  const { language } = useLanguage();
  const t = texts[language] || texts.es;
  
  const [notice, setNotice] = useState(null);
  const [loading, setLoading] = useState(true);
  const [accepting, setAccepting] = useState(false);
  const [showDeclineWarning, setShowDeclineWarning] = useState(false);
  const [scrolledToBottom, setScrolledToBottom] = useState(false);

  useEffect(() => {
    checkConsent();
  }, []);

  const checkConsent = async () => {
    try {
      const response = await axios.get('/consent/check');
      if (response.data.success) {
        const { requires_consent, notice: noticeData } = response.data.data;
        if (requires_consent && noticeData) {
          setNotice(noticeData);
        } else {
          onConsentGiven();
        }
      }
    } catch {
      onConsentGiven();
    } finally {
      setLoading(false);
    }
  };

  const handleAccept = async () => {
    if (!notice) return;
    setAccepting(true);
    try {
      await axios.post('/consent/accept', { notice_id: notice.id });
      onConsentGiven();
    } catch (err) {
      logger.error('Error accepting consent:', err);
    } finally {
      setAccepting(false);
    }
  };

  const handleDeclineConfirm = () => {
    logout();
  };

  const handleScroll = (e) => {
    const target = e.target;
    const isAtBottom = target.scrollHeight - target.scrollTop <= target.clientHeight + 30;
    if (isAtBottom) {
      setScrolledToBottom(true);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <Shield className="h-8 w-8 animate-pulse text-blue-600 mx-auto mb-3" />
          <p className="text-gray-500">{t.loading}</p>
        </div>
      </div>
    );
  }

  if (!notice) return null;

  const bodyContent = language === 'en' && notice.body_en ? notice.body_en : notice.body_es;
  const titleContent = language === 'en' && notice.title_en ? notice.title_en : notice.title_es;

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
      <Card className="w-full max-w-2xl shadow-lg" data-testid="consent-screen">
        <CardHeader className="text-center border-b">
          <div className="flex justify-center mb-2">
            <Shield className="h-8 w-8 text-blue-600" />
          </div>
          <CardTitle className="text-xl">{titleContent}</CardTitle>
          <p className="text-sm text-gray-500 mt-1">{t.subtitle}</p>
          <div className="flex justify-center gap-4 mt-2 text-xs text-gray-400">
            <span>{t.version} {notice.version}</span>
            <span>{t.jurisdiction}: {notice.jurisdiction}</span>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          <ScrollArea 
            className="h-80 p-6" 
            onScrollCapture={handleScroll}
            data-testid="consent-scroll-area"
          >
            <div className="prose prose-sm max-w-none whitespace-pre-wrap">
              {bodyContent}
            </div>
          </ScrollArea>
          {!scrolledToBottom && (
            <p className="text-xs text-center text-amber-600 py-2 bg-amber-50 border-t">
              {t.scrollToAccept}
            </p>
          )}
        </CardContent>
        <CardFooter className="flex flex-col gap-3 border-t pt-4">
          {showDeclineWarning ? (
            <div className="w-full bg-red-50 border border-red-200 rounded-lg p-4 text-center">
              <AlertTriangle className="h-5 w-5 text-red-500 mx-auto mb-2" />
              <p className="text-sm text-red-700 mb-3">{t.declineWarning}</p>
              <div className="flex gap-2 justify-center">
                <Button variant="outline" size="sm" onClick={() => setShowDeclineWarning(false)}>
                  {t.cancelDecline}
                </Button>
                <Button variant="destructive" size="sm" onClick={handleDeclineConfirm} data-testid="consent-confirm-decline-btn">
                  {t.confirmDecline}
                </Button>
              </div>
            </div>
          ) : (
            <div className="flex gap-3 w-full">
              <Button 
                variant="outline" 
                className="flex-1" 
                onClick={() => setShowDeclineWarning(true)}
                data-testid="consent-decline-btn"
              >
                {t.decline}
              </Button>
              <Button 
                className="flex-1 bg-blue-600 hover:bg-blue-700" 
                onClick={handleAccept}
                disabled={!scrolledToBottom || accepting}
                data-testid="consent-accept-btn"
              >
                {accepting ? '...' : t.accept}
              </Button>
            </div>
          )}
        </CardFooter>
      </Card>
    </div>
  );
};

export default ConsentScreen;
